import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Convertit une image (URL, meme relative/cookie-authentifiee) en dataURL —
// jsPDF ne sait embarquer qu'une dataURL/un element Image, pas une URL brute.
// "Meilleur effort" : toute erreur (pas de logo, format non lisible, CORS)
// renvoie null plutot que de faire echouer tout l'export PDF.
async function toDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
}

// Dimensions reelles d'une dataURL image, sans la reencoder (rapide, fichier
// de sortie plus petit) — utilise pour calculer le ratio largeur/hauteur
// avant de l'embarquer dans le PDF.
function loadImageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// Repasse l'image par un <canvas> avant de la donner a jsPDF : certains PNG
// (profil de couleur ICC, etc.) sont decodables par le navigateur mais pas
// par le decodeur PNG interne de jsPDF, qui echoue alors silencieusement
// (cf. le try/catch autour de addImage). Fallback UNIQUEMENT (cf.
// generateInvoicePdf) — canvas.toDataURL('image/png') reencode en PNG non
// compresse (l'image CGL passe de ~200 Ko a plus de 1,5 Mo), donc on essaie
// toujours d'abord la dataURL brute et on ne paie ce cout que si necessaire.
async function toCanvasPng(url) {
  const raw = await toDataUrl(url);
  if (!raw) return null;
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch (err) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = raw;
  });
}

// Dessine une image a une opacite donnee dans une boite `boxSize` (mm), en
// gardant son ratio largeur/hauteur. `opacity: 1` => logo d'en-tete net (`x`,
// `y` = coin haut-gauche) ; `opacity < 1` => watermark de fond (`x`, `y` =
// CENTRE de la boite). Leve une exception si jsPDF n'arrive pas a decoder
// `dataUrl` — a l'appelant de decider du fallback (cf. generateInvoicePdf).
function drawLogo(doc, dataUrl, { width, height }, { x, y, boxSize, opacity = 1, centered = false }) {
  const ratio = width / height;
  const w = ratio >= 1 ? boxSize : boxSize * ratio;
  const h = ratio >= 1 ? boxSize / ratio : boxSize;
  const drawX = centered ? x - w / 2 : x;
  const drawY = centered ? y - h / 2 : y;
  if (opacity < 1) {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity }));
  }
  doc.addImage(dataUrl, 'PNG', drawX, drawY, w, h);
  if (opacity < 1) {
    doc.restoreGraphicsState();
  }
}

/**
 * Genere et telecharge un PDF avec l'en-tete standard du portail (logo + nom de
 * la company, titre du rapport, date/heure de generation) suivi d'un ou
 * plusieurs tableaux. Convention obligatoire pour TOUT export PDF de prelote
 * (demande explicite de l'utilisateur, 2026-09-11 : "tout les pdf doivent avoir
 * le nom de leur company le logo") — un nouvel export PDF doit passer par cette
 * fonction plutot que d'appeler jsPDF directement.
 *
 * @param company   { name, logo } — `logo` est l'URL telle que renvoyee par
 *                  l'API (ex: company.logo du contexte auth) ; absent/invalide
 *                  -> le PDF part quand meme, juste sans image.
 * @param title     sous-titre affiche sous le nom de la company (ex: "Rapport
 *                  du Banque").
 * @param sections  [{ heading?, columns: string[], rows: (string|number)[][] }]
 *                  — un tableau par section ; `heading` optionnel au-dessus.
 * @param filename  nom du fichier telecharge (ex: "rapport-banque.pdf").
 */
export async function generateReportPdf({ company, title, sections, filename }) {
  const doc = new jsPDF();
  const marginLeft = 14;
  const logoDataUrl = await toDataUrl(company?.logo);
  const textX = logoDataUrl ? marginLeft + 26 : marginLeft;

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, marginLeft, 10, 20, 20);
    } catch (err) {
      // Format d'image que jsPDF ne sait pas decoder (ex: certains PNG avec
      // profil de couleur exotique) — on continue sans logo plutot que planter.
    }
  }

  doc.setFontSize(14);
  doc.text(company?.name || '', textX, 18);
  doc.setFontSize(11);
  doc.text(title, textX, 26);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleString('fr-FR'), 210 - marginLeft, 10, { align: 'right' });

  let y = 34;
  sections.forEach((section) => {
    if (section.heading) {
      doc.setFontSize(11);
      doc.text(section.heading, marginLeft, y);
      y += 6;
    }
    autoTable(doc, {
      startY: y,
      head: [section.columns],
      body: section.rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [74, 127, 181] },
      margin: { left: marginLeft, right: marginLeft },
    });
    y = doc.lastAutoTable.finalY + 10;
  });

  doc.save(filename);
}

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return `${d}/${m}/${y}`;
};

/**
 * PDF telechargeable de la page "Pay Online" (PaymentInvoicePage.jsx) —
 * reprend en jsPDF le meme contenu que l'apercu a l'ecran (logo CGL, adresse
 * CGL tiree du bloc Avis, "Facture a" avec les coordonnees de la company,
 * puis le tableau recapitulatif). Fonction dediee plutot que
 * generateReportPdf ci-dessus : celle-ci suit la mise en page "facture"
 * (en-tete a deux colonnes) au lieu du format rapport (logo + titre + table).
 *
 * @param notice        objet SiteNotice ({ center1_name, center1_lines })
 * @param company       company connectee ({ id, code, name, address, phone })
 * @param invoice       facture ({ id, period_start, period_end,
 *                      billable_bank_count, rate_per_bank, amount_due })
 * @param totalBalance  solde total du toutes factures (Number) — affiche une
 *                      ligne en plus si different du total de CETTE facture
 * @param filename      nom du fichier telecharge
 */
export async function generateInvoicePdf({ notice, company, invoice, totalBalance, filename }) {
  const doc = new jsPDF();
  const marginLeft = 14;
  const pageWidth = 210;
  const rightX = pageWidth - marginLeft;

  // Logo CGL : watermark en fond de page (meme intention que le CSS
  // .pip-invoice-watermark de l'apercu a l'ecran) + petit logo net en-tete
  // (convention generateReportPdf, "tout les pdf doivent avoir le nom de
  // leur company le logo") — les deux a partir de la MEME image chargee une
  // seule fois. Essaie d'abord la dataURL brute (fichier final plus leger) ;
  // si jsPDF n'arrive pas a la decoder, retente via un passage par canvas
  // (plus lourd mais plus tolerant) avant d'abandonner les deux.
  const logoUrl = `${process.env.PUBLIC_URL}/logo-cgl-transparent.png`;
  let logoDataUrl = await toDataUrl(logoUrl);
  let logoSize = logoDataUrl ? await loadImageSize(logoDataUrl) : null;
  let logoOk = false;
  if (logoDataUrl && logoSize) {
    try {
      drawLogo(doc, logoDataUrl, logoSize, { x: pageWidth / 2, y: 155, boxSize: 130, opacity: 0.08, centered: true });
      logoOk = true;
    } catch (err) {
      // retry via canvas ci-dessous
    }
  }
  if (!logoOk) {
    const canvasLogo = await toCanvasPng(logoUrl);
    if (canvasLogo) {
      try {
        drawLogo(doc, canvasLogo.dataUrl, canvasLogo, { x: pageWidth / 2, y: 155, boxSize: 130, opacity: 0.08, centered: true });
        logoDataUrl = canvasLogo.dataUrl;
        logoSize = canvasLogo;
        logoOk = true;
      } catch (err) {
        logoDataUrl = null;
      }
    } else {
      logoDataUrl = null;
    }
  }
  if (logoOk) {
    try {
      drawLogo(doc, logoDataUrl, logoSize, { x: marginLeft, y: 10, boxSize: 22 });
    } catch (err) {
      // best effort — le petit logo d'en-tete est secondaire au watermark
    }
  }
  const textX = logoOk ? marginLeft + 28 : marginLeft;

  // En-tete a 2 colonnes : adresse CGL a gauche, reference/dates a droite.
  // Hauteur du bloc gauche VARIABLE selon le nombre de lignes d'adresse —
  // la suite du document demarre APRES le plus grand des deux blocs, jamais
  // a une position fixe, pour ne jamais chevaucher un bloc sur l'autre.
  doc.setFontSize(13);
  doc.text(notice?.center1_name || 'CGL Multi-services', textX, 18);
  doc.setFontSize(8.5);
  let leftY = 24;
  (notice?.center1_lines || '').split('\n').filter(Boolean).forEach((line) => {
    doc.text(line, textX, leftY);
    leftY += 4.5;
  });

  // "→" (U+2192) n'est pas dans l'encodage des polices standard de jsPDF
  // (Helvetica/WinAnsi) — mal mesure a droite (align:'right'), ca decale le
  // texte et coupe une partie hors page. Separateur ASCII a la place, ici
  // seulement (l'ecran utilise '→' sans probleme, rendu par le navigateur).
  doc.setFontSize(9);
  doc.text(`Facture N° IVC-${company?.id}-${invoice.id}`, rightX, 18, { align: 'right' });
  doc.text(`Date : ${fmtDate(invoice.period_end)}`, rightX, 23, { align: 'right' });
  doc.text(`Période : ${fmtDate(invoice.period_start)} au ${fmtDate(invoice.period_end)}`, rightX, 28, { align: 'right' });
  const rightY = 28;

  let y = Math.max(leftY, rightY) + 10;
  doc.setFontSize(9);
  doc.text('Facturé à', marginLeft, y);
  y += 6;
  doc.setFontSize(11);
  doc.text(company?.name || '', marginLeft, y);
  y += 5;
  doc.setFontSize(9);
  if (company?.address) { doc.text(company.address, marginLeft, y); y += 5; }
  if (company?.phone) { doc.text(company.phone, marginLeft, y); y += 5; }

  const invoiceAmount = Number(invoice.amount_due || 0);
  const amountPaid = Number(invoice.amount_paid || 0);
  const creditApplied = Number(invoice.credit_applied || 0);
  const balanceAmount =
    Number(invoice.manual_balance || 0) + Number(invoice.carried_balance || 0);
  const totalPayable = Number(
    invoice.total_due ??
    (Number(invoice.carried_balance || 0) + Math.max(Number(invoice.balance || 0), 0))
  );

  const rows = [
    ['Code company', company?.code || '—'],
    ...(invoice.total_bank_count != null ? [['Nombre total de POS', String(invoice.total_bank_count)]] : []),
    ['Nombre de POS facturables', String(invoice.billable_bank_count)],
    ['Prix par POS', money(invoice.rate_per_bank)],
    ['Montant facture', money(invoiceAmount)],
    ['Montant reçu', money(amountPaid)],
    ['Crédit appliqué', money(creditApplied)],
    ['Crédit disponible', money(Number(invoice.credit_available || 0))],
    ['Solde antérieur', money(balanceAmount)],
    ['Montant à payer', money(totalPayable)],
  ];
  autoTable(doc, {
    startY: y + 6,
    head: [['Détail', 'Montant']],
    body: rows,
    styles: {
      fontSize: 9,
      textColor: [25, 30, 38],
      lineColor: [105, 115, 125],
      lineWidth: 0.2,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [31, 78, 121],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      lineColor: [31, 78, 121],
    },
    alternateRowStyles: { fillColor: [242, 246, 250] },
    margin: { left: marginLeft, right: marginLeft },
  });


  // Liste des POS facturables
  const billablePos = Array.isArray(invoice.billable_pos)
    ? invoice.billable_pos
    : [];

  const posRows = billablePos.length
    ? billablePos.map((pos) => {
        const rawDate = pos.qualification_date;
        let qualificationDate = "—";
        if (rawDate) {
          const parsedDate = new Date(`${String(rawDate).slice(0, 10)}T00:00:00`);
          if (!Number.isNaN(parsedDate.getTime())) {
            qualificationDate = parsedDate.toLocaleDateString("fr-FR");
          }
        }
        return [
          String(pos.pos_number ?? "—"),
          String(pos.pos_name || "—"),
          qualificationDate,
          String(pos.valid_ticket_count ?? 0),
        ];
      })
    : [["—", "Aucun POS facturable", "—", "—"]];

  let posTitleY = (doc.lastAutoTable?.finalY || y + 6) + 10;
  const posPageHeight = doc.internal.pageSize.getHeight();

  if (posTitleY + 18 > posPageHeight - 12) {
    doc.addPage();
    posTitleY = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Liste des POS facturables", marginLeft, posTitleY);

  autoTable(doc, {
    startY: posTitleY + 4,
    head: [["POS", "Nom", "Date de qualification", "Tickets valides"]],
    body: posRows,
    styles: {
      fontSize: 8,
      cellPadding: 2.5,
      textColor: [25, 30, 38],
      lineColor: [105, 115, 125],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [31, 78, 121],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      lineColor: [31, 78, 121],
    },
    alternateRowStyles: { fillColor: [242, 246, 250] },
    margin: { left: marginLeft, right: marginLeft },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 55 },
      2: { cellWidth: 48 },
      3: { cellWidth: 28 },
    },
  });

  // Principes de facturation
  const principlesPageHeight = doc.internal.pageSize.getHeight();
  const principlesMargin = 14;
  const principlesWidth = doc.internal.pageSize.getWidth() - principlesMargin * 2;
  let principlesY = (doc.lastAutoTable?.finalY || 20) + 10;

  const billingPrinciples = [
    "Une POS devient facturable des qu'elle atteint 5 tickets imprimes valides et non supprimes.",
    "La periode commence a la date ou la POS atteint le seuil de 5 tickets.",
    "Le paiement est du 30 jours apres le debut de la periode.",
    "Le solde anterieur est reporte sans compter deux fois la meme dette.",
    "Les paiements approuves reglent d'abord les factures impayees les plus anciennes.",
    "Tout excedent de paiement reste en credit pour les factures suivantes.",
    "Les paiements rejetes ne reduisent pas le solde du.",
    "Le solde final tient compte des montants factures, paiements et credits appliques."
  ];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  if (principlesY + 8 > principlesPageHeight - 12) {
    doc.addPage();
    principlesY = 20;
  }
  doc.text("Principes de facturation", principlesMargin, principlesY);
  principlesY += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  for (const principle of billingPrinciples) {
    const lines = doc.splitTextToSize("- " + principle, principlesWidth);
    const blockHeight = lines.length * 4.2 + 2;
    if (principlesY + blockHeight > principlesPageHeight - 12) {
      doc.addPage();
      principlesY = 18;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Principes de facturation (suite)", principlesMargin, principlesY);
      principlesY += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.text(lines, principlesMargin, principlesY);
    principlesY += blockHeight;
  }

  doc.save(filename);
}
